import {
	Button,
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	useFilterBuilder,
	useUrlState,
} from "@monbits/ui";
import { useQuery } from "@tanstack/react-query";
import { Check, CheckCircle, Copy, Network, ShieldOff } from "lucide-react";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import { BLOCKCHAIN_NAMES } from "src/constants/blockchains";
import walletProvider, {
	type InboundWalletsResponse,
	type WalletDetails,
} from "src/providers/wallet-provider";
import { chainShortOptions } from "src/utils/options";

const FILTER_FIELDS: FilterField[] = [
	{
		id: "blockchain",
		label: "Network",
		type: "select",
		icon: <Network className="h-4 w-4" />,
		options: chainShortOptions,
	},
];

export function InboundWalletsList() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);

	const search = searchParams.get("search") || "";

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const [pagination, setPagination] = useUrlState({
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const offset = (pagination.page - 1) * pagination.limit;

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: { blockchain?: string } = {};

		for (const f of filters) {
			if (f.operator === "is" && f.field === "blockchain") {
				result.blockchain = f.value as string;
			}
		}

		return result;
	}, [filters]);

	const { data, isLoading } = useQuery({
		queryKey: [
			"inbound-wallets",
			search,
			apiFilters.blockchain,
			pagination.page,
			pagination.limit,
		],
		queryFn: async () => {
			const response: InboundWalletsResponse =
				await walletProvider.listInboundWallets({
					limit: pagination.limit,
					offset,
					search,
					blockchain: apiFilters.blockchain,
				});
			return response;
		},
	});

	const totalCount = data?.pagination.totalCount ?? 0;
	const wallets = data?.wallets ?? [];

	const handleViewWallet = React.useCallback(
		(wallet: WalletDetails) => {
			const currentUrl = new URL(window.location.href);
			const queryString = currentUrl.search;
			const targetPath = queryString
				? `/wallets/inbound/${wallet.id}${queryString}`
				: `/wallets/inbound/${wallet.id}`;
			navigate(targetPath);
		},
		[navigate],
	);

	const handleCopyAddress = React.useCallback(
		(address: string, e: React.MouseEvent) => {
			e.stopPropagation();
			navigator.clipboard.writeText(address);
			setCopiedAddress(address);
			toast.success("Address copied to clipboard");
			setTimeout(() => setCopiedAddress(null), 2000);
		},
		[],
	);

	const columns: ColumnDef<WalletDetails>[] = React.useMemo(
		() => [
			{
				id: "network",
				header: "Network",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<CryptoIcon ticker={row.blockchain} className="h-5 w-5" />
						<span className="whitespace-nowrap">
							{BLOCKCHAIN_NAMES[row.blockchain] || row.blockchain}
						</span>
					</div>
				),
			},
			{
				id: "address",
				header: "Address",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<span className="font-mono text-sm">{row.address}</span>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6"
							onClick={(e) => handleCopyAddress(row.address, e)}
						>
							{copiedAddress === row.address ? (
								<Check className="h-3 w-3 text-green-600" />
							) : (
								<Copy className="h-3 w-3" />
							)}
						</Button>
					</div>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: (row) =>
					row.blockedAt ? (
						<StatusBadge type="error" icon={ShieldOff}>
							Blocked
						</StatusBadge>
					) : row.transferSuspendedAt ? (
						<StatusBadge type="warning" icon={ShieldOff}>
							Transfer Suspended
						</StatusBadge>
					) : (
						<StatusBadge type="success" icon={CheckCircle}>
							Active
						</StatusBadge>
					),
			},
			{
				id: "created",
				header: "Created",
				cell: (row) => <TimeLabel time={row.createdAt} />,
			},
			{
				id: "transactions",
				header: "Transactions",
				cell: (row) => (
					<div className="text-sm">
						Mainnet: {row.transactionCounts?.mainnet?.confirmed || 0}
						{row.transactionCounts?.testnet &&
							row.transactionCounts.testnet.confirmed > 0 && (
								<span className="text-muted-foreground ml-2">
									(Testnet: {row.transactionCounts.testnet.confirmed})
								</span>
							)}
					</div>
				),
			},
		],
		[copiedAddress, handleCopyAddress],
	);

	const hasActiveFilters = search || filters.length > 0;

	return (
		<>
			<div className="flex gap-2 mb-4 flex-wrap items-center">
				<SearchInput placeholder="Search by address..." className="w-[300px]" />
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={wallets}
				isLoading={isLoading}
				onRowClick={handleViewWallet}
				getRowId={(row) => String(row.id)}
				emptyMessage={
					hasActiveFilters ? "No wallets found" : "No inbound wallets"
				}
				emptyDescription={
					hasActiveFilters
						? "Try adjusting your search criteria"
						: "Inbound wallets will appear here"
				}
				pagination={
					totalCount > 0
						? {
								page: pagination.page,
								pageSize: pagination.limit,
								totalCount,
								onPageChange: (page: number) => setPagination({ page }),
								onPageSizeChange: (limit: number) =>
									setPagination({ limit, page: 1 }),
							}
						: undefined
				}
			/>

			<Outlet />
		</>
	);
}
